import { useEffect, useState } from "react";
import { collectionGroup, query, where, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { AvailabilityStatus } from "@/lib/types";

/**
 * Preloads ALL of the current user's availability across all matches in one query.
 * Returns a Map<matchId, status> so cards can render with correct state immediately.
 */
export function useMyAvailability(clubId: string | undefined, userId: string | undefined) {
  const [statusMap, setStatusMap] = useState<Map<string, AvailabilityStatus>>(new Map());

  useEffect(() => {
    if (!clubId || !userId) { setStatusMap(new Map()); return; }

    const q = query(
      collectionGroup(db, "availability"),
      where("clubId", "==", clubId),
      where("userId", "==", userId)
    );

    const unsub = onSnapshot(q, (snap) => {
      const map = new Map<string, AvailabilityStatus>();
      snap.docs.forEach((d) => {
        // Parent path: clubs/{clubId}/matches/{matchId}/availability/{userId}
        const matchId = d.ref.parent.parent?.id;
        if (matchId) map.set(matchId, d.data().status as AvailabilityStatus);
      });
      setStatusMap(map);
    });

    return unsub;
  }, [clubId, userId]);

  return statusMap;
}

/**
 * Preloads ALL of the current user's training attendance across all trainings.
 * Returns a Map<trainingId, "in" | "out">.
 */
export function useMyTrainingAttendance(clubId: string | undefined, userId: string | undefined) {
  const [statusMap, setStatusMap] = useState<Map<string, "in" | "out">>(new Map());

  useEffect(() => {
    if (!clubId || !userId) { setStatusMap(new Map()); return; }

    const q = query(
      collectionGroup(db, "attendance"),
      where("clubId", "==", clubId),
      where("userId", "==", userId)
    );

    const unsub = onSnapshot(q, (snap) => {
      const map = new Map<string, "in" | "out">();
      snap.docs.forEach((d) => {
        const trainingId = d.ref.parent.parent?.id;
        if (trainingId) map.set(trainingId, d.data().status as "in" | "out");
      });
      setStatusMap(map);
    });

    return unsub;
  }, [clubId, userId]);

  return statusMap;
}
